import { toMerged } from 'es-toolkit/object';
import { immerable, produce } from 'immer';

import { IMap } from 'helpers/types';
import { FnRecipe } from 'state_management/recoilStates/helpers';

import { RoleAlias, Timestamps } from '../core';
import { IUserResponse } from './responses';
import { ProjectFilterBy, ProjectsSortOrder, UserSettings } from './types';

export const DEFAULT_USER_SETTINGS: UserSettings = {
  projectsSortOrder: ProjectsSortOrder.ALPHABETICAL,
  projectsFilterBy: ProjectFilterBy.NONE
};

export class User {
  [immerable] = true;
  readonly id: string;
  readonly authUserID: string;
  readonly email: string;
  readonly name: string;
  readonly organizationID: string;
  readonly organizationName: string;
  readonly timestamps: Timestamps;
  readonly roleIDs: string[];
  readonly roleAlias: RoleAlias;
  readonly settings: UserSettings;
  constructor(id: string, authUserID: string, email: string, name: string, organizationID: string, organizationName: string, timestamps: Timestamps, roleIDs: string[], roleAlias: RoleAlias, settings: UserSettings) {
    this.id = id;
    this.authUserID = authUserID;
    this.email = email;
    this.name = name;
    this.organizationID = organizationID;
    this.organizationName = organizationName;
    this.timestamps = timestamps;
    this.roleIDs = [...roleIDs].sort();
    this.roleAlias = roleAlias;
    this.settings = settings;
  }
  get nameFormatted() {
    return this.name.trim() || 'Unnamed User';
  }
  static equals(a: User, b: User) {
    return JSON.stringify(a.toJson()) === JSON.stringify(b.toJson());
  }
  private _serializeSettings(): IMap {
    return this.settings;
  }
  private static _deserializeSettings(json: IMap): UserSettings {
    return json as UserSettings;
  }
  toJson(): IUserResponse {
    return {
      id: this.id,
      userId: this.authUserID,
      email: this.email,
      name: this.name,
      orgId: this.organizationID,
      orgName: this.organizationName,
      timestamps: this.timestamps.toJson(),
      roles: this.roleIDs.map((id) => ({ id })),
      roleAlias: this.roleAlias,
      settings: this._serializeSettings()
    };
  }
  static fromJson(json: IUserResponse): User {
    return new User(
      json.id,
      json.userId,
      json.email,
      json.name,
      json.orgId,
      json.orgName,
      Timestamps.fromJson(json.timestamps),
      (json.roles || []).map((it) => it.id),
      json.roleAlias,
      // Merge with defaults ensures that even if the server doesn't give back a setting key, we will still have it.
      // The only case in which a default is not populated when the key is optional in UserSettings
      // and no default value has been specified for it in DEFAULT_USER_SETTINGS.
      toMerged(DEFAULT_USER_SETTINGS, User._deserializeSettings(json.settings || {}))
    );
  }

  /**
   * Accepts an Immer recipe to update some properties and return a new immutable instance.
   */
  update(fnRecipe: FnRecipe<User>): User {
    return produce(this, fnRecipe);
  }
}
